import {
  Box,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import './ArrReviewMetrics.scss';
import {t} from 'i18next';
import ArrTooltipWrapper from '../arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as VerticalDivider} from '../../../assets/images/vertical-divider.svg';
import {ReactComponent as HorizontalDivider} from '../../../assets/images/horizontal-divider.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {IGetAIReviewJobResultResponse} from '../../../api/types/aiReview.type';

type MetricsDetails = {
  type: 'Relevancy' | 'Issue Coding';
  handleDocErrorPanelOpen?: () => void;
  metricsDetails?: IGetAIReviewJobResultResponse;
};
const fileTypes = [
  t('ai.review.metrics.csvType'),
  t('ai.review.metrics.multiMediaType'),
  t('ai.review.metrics.ArchieveType'),
  t('ai.review.metrics.databaseType'),
  t('ai.review.metrics.unKnownType'),
  t('ai.review.metrics.systemType'),
];
const ArrReviewMetrics: React.FC<MetricsDetails> = ({
  type,
  handleDocErrorPanelOpen,
  metricsDetails,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`
  );
  const handleMetricKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.currentTarget !== e.target) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent page scrolling on Space
      handleDocErrorPanelOpen?.();
    }
  };
  return (
    <Box
      className="containerBox"
      component="section"
      data-testid={`arr-review-metrics-container`}
    >
      <Box
        className="innerContainer"
        data-testid={`arr-review-metrics-inner-container`}
      >
        <Box
          className="dataBox"
          role="group"
          component="section"
          data-testid={`arr-review-metrics-dataBox`}
        >
          <Box
            className="sourceBox"
            tabIndex={0}
            aria-label={`${t('ai.review.metrics.docInSelectedSource')} ${metricsDetails?.totalDocCount}`}
            data-testid={`arr-review-metrics-sourceBox`}
          >
            <Typography
              className="sourceCount"
              component="h2"
              aria-hidden="true"
              data-testid={`arr-review-metrics-selected-source-${type}`}
            >
              {metricsDetails?.totalDocCount}
            </Typography>
            <Typography
              className="sourceText"
              component="p"
              aria-hidden="true"
              data-testid={`arr-review-metrics-doc-in-selected-source`}
            >
              {t('ai.review.metrics.docInSelectedSource')}
            </Typography>
          </Box>
          <Divider
            className="dividerLine"
            role="separator"
            aria-hidden="true"
          />
          <Box
            className="excludedAiBox"
            tabIndex={0}
            aria-label={`${t('ai.review.metrics.aiExcluded')} ${metricsDetails?.excludedCount}`}
            data-testid={`arr-review-metrics-ai-exclude-box`}
          >
            <Typography
              className="excludedAiCount"
              component="h3"
              aria-hidden="true"
              data-testid={`arr-review-metrics-typography-ai-excluded--${type}`}
            >
              {metricsDetails?.excludedCount}
            </Typography>
            <Typography
              className="excludedAiText"
              component="p"
              aria-hidden="true"
              data-testid={`arr-review-metrics-ai-excluded-dummy-tooltip-text`}
            >
              {t('ai.review.metrics.aiExcluded')}
              <ArrTooltipWrapper
                trigger={
                  <InfoIcon
                    className="metricsToolTip"
                    data-testid="toggle-info-tooltip"
                    tabIndex={0}
                    aria-label="information icon"
                    role="button"
                  />
                }
                title={t('ai.review.metrics.ecludedAiTooltipText')}
              >
                {t('ai.review.metrics.ecludedAiTooltipText')}
                <Stack component="ul" className="excludedAiTooltipText">
                  {fileTypes.map((type, index) => (
                    <Box key={index} component="li">
                      <Typography className="typo-text body4_size">
                        {type}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </ArrTooltipWrapper>
            </Typography>
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <Box className="dividerWrapper">
          <HorizontalDivider
            aria-hidden="true"
            focusable="false"
            className="horizontalDivider"
          />
        </Box>
      )}
      <Box
        className="innerContainer"
        data-testid={`arr-review-metrics-inner-container`}
      >
        {!isMobile && <VerticalDivider aria-hidden="true" focusable="false" />}
        <Box
          className="dataBox"
          role="group"
          component="section"
          data-testid={`arr-review-metrics-data-box`}
        >
          <Box
            className="sourceBox"
            tabIndex={0}
            aria-label={`${t('ai.review.metrics.aiSubmitted')} ${metricsDetails?.submittedCount}`}
            data-testid={`arr-review-metrics-submitted-count-box`}
          >
            <Typography
              className="sourceCount"
              component="h2"
              aria-hidden="true"
              data-testid={`arr-review-metrics-ai-subitted-${type}`}
            >
              {metricsDetails?.submittedCount}
            </Typography>
            <Typography
              className="sourceText"
              component="p"
              aria-hidden="true"
              data-testid={`arr-review-metrics-ai-submitted`}
            >
              {t('ai.review.metrics.aiSubmitted')}
            </Typography>
          </Box>
          {type === 'Relevancy' && (
            <>
              <Box>
                <Divider
                  className="dividerLine"
                  role="separator"
                  aria-hidden="true"
                />
              </Box>
              <Box className="excludedAiBoxWrapper">
                <Box
                  className="excludedAiBox"
                  tabIndex={0}
                  role="button"
                  aria-label={`${t('common.clickable')} ${t('ai.review.metrics.errored')} ${metricsDetails?.erroredCount}`}
                  onClick={handleDocErrorPanelOpen}
                  onKeyDown={handleMetricKeyDown}
                  data-testid={`arr-review-metrics-error-box`}
                >
                  <Typography
                    className="excludedAiCount"
                    component="h3"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-error-count`}
                  >
                    {metricsDetails?.erroredCount}
                  </Typography>
                  <Typography
                    className="excludedAiText"
                    component="p"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-error-text`}
                  >
                    {t('ai.review.metrics.errored')}
                  </Typography>
                </Box>
                <Box
                  className="excludedAiTooltipBox"
                  data-testid={`arr-review-metrics-tooltip-wrappers`}
                >
                  <ArrTooltipWrapper
                    trigger={
                      <InfoIcon
                        className="metricsToolTip"
                        data-testid="toggle-info-tooltip"
                        tabIndex={0}
                        role="button"
                        aria-label="information icon"
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    title={t('ai.review.metrics.erroredTooltipText')}
                  >
                    {t('ai.review.metrics.erroredTooltipText')}
                  </ArrTooltipWrapper>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      {isMobile && (
        <Box className="dividerWrapper">
          <HorizontalDivider
            aria-hidden="true"
            focusable="false"
            className="horizontalDivider"
          />
        </Box>
      )}
      <Box
        className="innerContainer"
        data-testid={`arr-review-metrics-inner-container`}
      >
        {!isMobile && <VerticalDivider aria-hidden="true" focusable="false" />}
        <Box
          className="dataBox"
          role="group"
          component="section"
          data-testid={`arr-review-metrics-data-box`}
        >
          <Box
            className="sourceBox"
            tabIndex={0}
            aria-label={`${t('ai.review.metrics.completed')} ${metricsDetails?.completedCount}`}
            data-testid={`arr-review-metrics-completed-count-box`}
          >
            <Typography
              className="sourceCount completed"
              component="h2"
              aria-hidden="true"
              data-testid={`arr-review-metrics-completed-${type}`}
            >
              {metricsDetails?.completedCount}
            </Typography>
            <Typography
              className="sourceText"
              component="p"
              aria-hidden="true"
              data-testid={`arr-review-metrics-completed`}
            >
              {t('ai.review.metrics.completed')}
            </Typography>
          </Box>
          <Divider
            className="dividerLine"
            role="separator"
            aria-hidden="true"
          />
          <Box
            className="excludedAiBox"
            data-testid={`arr-review-metrics-data-box`}
          >
            {type === 'Relevancy' ? (
              <Box className="relavantBox">
                <Box
                  tabIndex={0}
                  aria-label={`${t('ai.review.metrics.relevant')} ${metricsDetails?.relevantDocCount}`}
                  data-testid={`arr-review-metrics-relevant-doc-count-box`}
                >
                  <Typography
                    className="excludedAiCount completed"
                    component="h3"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-relevant-${type}`}
                  >
                    {metricsDetails?.relevantDocCount}
                  </Typography>
                  <Typography
                    className="excludedAiText"
                    component="p"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-relevant`}
                  >
                    {t('ai.review.metrics.relevant')}
                  </Typography>
                </Box>
                <Box
                  tabIndex={0}
                  aria-label={`${t('ai.review.metrics.notRelevant')} ${metricsDetails?.nonRelevantDocCount}`}
                  data-testid={`arr-review-metrics-not-relevant-doc-count-box`}
                >
                  <Typography
                    className="excludedAiCount completed"
                    component="h3"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-not-relevant-${type}`}
                  >
                    {metricsDetails?.nonRelevantDocCount}
                  </Typography>
                  <Typography
                    className="excludedAiText"
                    component="p"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-not-relevant`}
                  >
                    {t('ai.review.metrics.notRelevant')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                className="excludedAiBoxWrapper"
                data-testid={`arr-review-metrics-exclude-ai-box`}
              >
                <Box
                  className="excludedAiBox"
                  tabIndex={0}
                  role="button"
                  aria-label={`${t('common.clickable')} ${t('ai.review.metrics.errored')} ${metricsDetails?.erroredCount}`}
                  onClick={handleDocErrorPanelOpen}
                  onKeyDown={handleMetricKeyDown}
                  data-testid={`arr-review-metrics-error-box`}
                >
                  <Typography
                    className="excludedAiCount"
                    component="h3"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-error-count`}
                  >
                    {metricsDetails?.erroredCount}
                  </Typography>
                  <Typography
                    className="excludedAiText"
                    component="p"
                    aria-hidden="true"
                    data-testid={`arr-review-metrics-error-text`}
                  >
                    {t('ai.review.metrics.errored')}
                  </Typography>
                </Box>
                <Box
                  className="excludedAiTooltipBox"
                  data-testid={`arr-review-metrics-tooltip-box`}
                >
                  <ArrTooltipWrapper
                    trigger={
                      <InfoIcon
                        className="metricsToolTip"
                        data-testid="toggle-info-tooltip"
                        tabIndex={0}
                        role="button"
                        aria-label="information icon"
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    title={t('ai.review.metrics.erroredTooltipText')}
                  >
                    {t('ai.review.metrics.erroredTooltipText')}
                  </ArrTooltipWrapper>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default ArrReviewMetrics;
